import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateExpensePayload,
  CreateReimbursementPayload,
  ExpenseComment,
  ExpenseDetail,
  ExpenseListItem,
  PaginatedResponse,
  ReimbursementEvent,
  UpdateExpensePayload,
  UpdateReimbursementPayload,
} from "../types";

const EXPENSES_KEY = ["expenses"] as const;

export type ReimbursementStatus =
  | "not_required"
  | "pending"
  | "partial"
  | "complete";

export type ExpenseListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  category?: string;
  companyId?: string;
  vendorCompanyId?: string;
  vendorContactId?: string;
  productId?: string;
  productIncludeProjectLinks?: boolean;
  projectId?: string;
  budgetId?: string;
  meetingId?: string;
  accessVaultId?: string;
  assetId?: string;
  submittedById?: string;
  paidById?: string;
  isReimbursable?: boolean;
  reimbursementStatus?: ReimbursementStatus[];
  dateFrom?: string;
  dateTo?: string;
};

export function useExpenseList(
  params: ExpenseListParams = {},
): UseQueryResult<PaginatedResponse<ExpenseListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...EXPENSES_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<ExpenseListItem>>("/expenses", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          category: params.category,
          companyId: params.companyId,
          vendorCompanyId: params.vendorCompanyId,
          vendorContactId: params.vendorContactId,
          productId: params.productId,
          productIncludeProjectLinks:
            params.productIncludeProjectLinks === undefined
              ? undefined
              : String(params.productIncludeProjectLinks),
          projectId: params.projectId,
          budgetId: params.budgetId,
          meetingId: params.meetingId,
          accessVaultId: params.accessVaultId,
          assetId: params.assetId,
          submittedById: params.submittedById,
          paidById: params.paidById,
          isReimbursable:
            params.isReimbursable === undefined
              ? undefined
              : String(params.isReimbursable),
          reimbursementStatus: params.reimbursementStatus,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
        },
      }),
  });
}

export function useExpense(id: string): UseQueryResult<ExpenseDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...EXPENSES_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ expense: ExpenseDetail }>(
        `/expenses/${id}`,
        { token: accessToken },
      );
      return res.expense;
    },
  });
}

export function useCreateExpense() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateExpensePayload) =>
      apiFetch<{ expense: ExpenseDetail }>("/expenses", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...EXPENSES_KEY] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["vault"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useUpdateExpense(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateExpensePayload) =>
      apiFetch<{ expense: ExpenseDetail }>(`/expenses/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...EXPENSES_KEY] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
      qc.invalidateQueries({ queryKey: ["vault"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useApproveExpense() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ expense: ExpenseDetail }>(`/expenses/${id}/status`, {
        method: "PUT",
        body: { status: "approved" },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...EXPENSES_KEY] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
    },
  });
}

export function useRejectExpense() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason: string }) =>
      apiFetch<{ expense: ExpenseDetail }>(`/expenses/${id}/status`, {
        method: "PUT",
        body: { status: "rejected", rejectionReason },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...EXPENSES_KEY] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
    },
  });
}

export function useDeleteExpense() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/expenses/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...EXPENSES_KEY] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
    },
  });
}

export function useExpenseComments(expenseId: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...EXPENSES_KEY,
      expenseId,
      "comments",
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!expenseId,
    queryFn: async () => {
      const res = await apiFetch<{ data: ExpenseComment[] }>(
        `/expenses/${expenseId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddExpenseComment(expenseId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: ExpenseComment }>(
        `/expenses/${expenseId}/comments`,
        {
          method: "POST",
          body: { content },
          token: accessToken,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...EXPENSES_KEY, expenseId, "comments"],
      });
    },
  });
}

export function useDeleteExpenseComment(expenseId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/expenses/${expenseId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...EXPENSES_KEY, expenseId, "comments"],
      });
    },
  });
}

// ─── Reimbursement event hooks ────────────────────────────────

export function useReimbursementList(expenseId: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      ...EXPENSES_KEY,
      expenseId,
      "reimbursements",
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!expenseId,
    queryFn: async () => {
      const res = await apiFetch<{ data: ReimbursementEvent[] }>(
        "/reimbursements",
        { token: accessToken, query: { expenseId } },
      );
      return res.data;
    },
  });
}

function invalidateExpenseAndReimb(
  qc: ReturnType<typeof useQueryClient>,
  expenseId: string,
) {
  qc.invalidateQueries({ queryKey: [...EXPENSES_KEY, "detail", expenseId] });
  qc.invalidateQueries({ queryKey: [...EXPENSES_KEY, "list"] });
  qc.invalidateQueries({
    queryKey: [...EXPENSES_KEY, expenseId, "reimbursements"],
  });
  qc.invalidateQueries({ queryKey: ["budgets"] });
}

export function useCreateReimbursement(expenseId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateReimbursementPayload) =>
      apiFetch<{ reimbursement: ReimbursementEvent }>("/reimbursements", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateExpenseAndReimb(qc, expenseId),
  });
}

export function useUpdateReimbursement(expenseId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateReimbursementPayload;
    }) =>
      apiFetch<{ reimbursement: ReimbursementEvent }>(`/reimbursements/${id}`, {
        method: "PATCH",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateExpenseAndReimb(qc, expenseId),
  });
}

export function useDeleteReimbursement(expenseId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/reimbursements/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => invalidateExpenseAndReimb(qc, expenseId),
  });
}
